import { useState, useEffect } from 'react';

/**
 * Shared hook for mobile/tablet detection.
 * Returns { isMobile, isTablet, isDesktop, isTouch, width }
 *
 * Breakpoints (matches Tailwind defaults):
 *   mobile:  < 768px
 *   tablet:  768px – 1023px
 *   desktop: >= 1024px
 */
export default function useIsMobile() {
  const [state, setState] = useState(() => compute());

  function compute() {
    const w = window.innerWidth;
    const isTouch = window.matchMedia('(pointer: coarse)').matches;
    // A phone whose page content is wider than the screen zooms out, which
    // makes innerWidth report > 768 and flipped the app into its desktop
    // layout (seen on iPhone 16 Pro Max). A touch device whose physical
    // screen is narrower than 768 is a phone regardless of innerWidth.
    const screenW = Math.min(window.screen?.width || w, window.screen?.height || w);
    const isMobile = w < 768 || (isTouch && screenW < 768);
    return {
      width: w,
      isMobile,
      isTablet: !isMobile && w >= 768 && w < 1024,
      isDesktop: !isMobile && w >= 1024,
      isTouch,
    };
  }

  useEffect(() => {
    let raf;
    const onResize = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => setState(compute()));
    };
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      cancelAnimationFrame(raf);
    };
  }, []);

  return state;
}
